import { type CallingReportRow } from '@utazzo/shared';
import { CalendarRange, ChevronDown, ChevronRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { CR_TOKENS_CSS } from './callsense-tokens';

/**
 * Daily, user-wise calling report - a faithful build of the CallSense 2.0
 * design: Total -> TL -> agents, merged-header groups Tasks / Calling hours /
 * First-call quality / Follow-up quality / Outbound / Inbound (27 columns).
 * Columns whose data source is not live yet (per-first-call / per-follow-up
 * talk minutes, and CallerDesk missed/declined) keep the design's dashed
 * "coming online" treatment rather than faking a number. Agent talk % and the
 * quality scores are real (overall, not yet split first-vs-follow-up).
 */

const H = (m: number): string => (m / 60).toFixed(1);
const score5 = (s: number | null): number | null =>
  s == null ? null : Math.round((s / 20) * 10) / 10;

const DASH = '2px dashed var(--cr-warn)';

type Sub = { label: string; bold?: boolean; br?: boolean; dash?: boolean };
const SUBHEADS: Sub[] = [
  { label: 'Today follow-ups' },
  { label: 'Fresh leads' },
  { label: 'Missed' },
  { label: 'Total tasks', bold: true, br: true },
  { label: 'Achieved' },
  { label: 'Achieved %', bold: true, br: true },
  { label: 'CallerDesk' },
  { label: 'WhatsApp' },
  { label: 'Total', bold: true, br: true },
  { label: 'Agent talk', dash: true },
  { label: 'Cust. talk', dash: true },
  { label: 'Agent %' },
  { label: 'Cust. %' },
  { label: 'Score', bold: true, br: true },
  { label: 'Agent talk', dash: true },
  { label: 'Cust. talk', dash: true },
  { label: 'Agent %' },
  { label: 'Cust. %' },
  { label: 'Score', bold: true, br: true },
  { label: 'Total', bold: true },
  { label: 'Acc' },
  { label: 'Missed', dash: true },
  { label: 'Declined', dash: true, br: true },
  { label: 'Total', bold: true },
  { label: 'Acc' },
  { label: 'Missed', dash: true },
  { label: 'Declined', dash: true },
];

type Group = { tl: CallingReportRow; agents: CallingReportRow[] };

export const CallingReportTable = ({
  rows,
  loading,
  onPickAgent,
  activeAgent,
}: {
  rows: CallingReportRow[];
  loading: boolean;
  onPickAgent?: (name: string) => void;
  activeAgent?: string | null;
}): React.JSX.Element => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { total, groups } = useMemo(() => {
    const total = rows.find((r) => r.kind === 'total') ?? null;
    const tls = rows.filter((r) => r.kind === 'tl');
    const groups: Group[] = tls.map((tl) => ({
      tl,
      agents: rows.filter((r) => r.kind === 'agent' && r.parentId === tl.id),
    }));
    return { total, groups };
  }, [rows]);

  const toggle = (id: string): void =>
    setExpanded((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allOpen = groups.length > 0 && expanded.size === groups.length;

  const scoreColor = (v: number | null): string =>
    v == null
      ? 'var(--cr-mut)'
      : v >= 4
        ? 'var(--cr-ok)'
        : v >= 3
          ? 'var(--cr-warn)'
          : 'var(--cr-bad)';

  /** One <td>. */
  const Cell = ({
    children,
    bold,
    br,
    color,
    bg,
    den,
    pill,
  }: {
    children: React.ReactNode;
    bold?: boolean;
    br?: boolean;
    color?: string;
    bg?: string;
    den?: string;
    pill?: { bg: string; color: string };
  }): React.JSX.Element => (
    <td
      style={{
        padding: '0 8px',
        textAlign: 'right',
        whiteSpace: 'nowrap',
        fontWeight: bold ? 800 : 500,
        color: color ?? 'var(--cr-tx)',
        background: bg ?? 'transparent',
        borderRight: br ? '1px solid var(--cr-bd)' : undefined,
        borderBottom: '1px solid var(--cr-bd2)',
      }}
    >
      {pill ? (
        <span
          style={{
            borderRadius: 6,
            padding: '2px 7px',
            background: pill.bg,
            color: pill.color,
            fontWeight: 800,
          }}
        >
          {children}
        </span>
      ) : (
        <span className="tabular-nums">{children}</span>
      )}
      {den ? (
        <span style={{ fontSize: 12, color: 'var(--cr-mut)', marginLeft: 3 }}>{den}</span>
      ) : null}
    </td>
  );

  const dashCell = (): React.JSX.Element => (
    <td
      title="Per-call talk split comes online with the AI pipeline"
      style={{
        padding: '0 8px',
        textAlign: 'center',
        color: 'var(--cr-mut)',
        opacity: 0.6,
        borderBottom: '1px solid var(--cr-bd2)',
      }}
    >
      -
    </td>
  );

  const cells = (r: CallingReportRow): React.JSX.Element => {
    const achPct = r.tasks.total > 0 ? Math.round((r.tasks.achieved / r.tasks.total) * 100) : null;
    const pill =
      achPct == null
        ? { bg: 'transparent', color: 'var(--cr-mut)' }
        : achPct >= 80
          ? { bg: 'var(--cr-okbg)', color: 'var(--cr-ok)' }
          : achPct >= 50
            ? { bg: 'var(--cr-warnbg)', color: 'var(--cr-warn)' }
            : { bg: 'var(--cr-badbg)', color: 'var(--cr-bad)' };
    const sc = score5(r.avgScore);
    const aPct = r.agentTalkPct;
    const cPct = aPct == null ? null : 100 - aPct;
    const aPctColor = aPct != null && (aPct > 70 || aPct < 30) ? 'var(--cr-bad)' : 'var(--cr-tx)';
    const dashBg = 'color-mix(in srgb, var(--cr-warn) 7%, transparent)';
    return (
      <>
        {/* Tasks */}
        <Cell>{r.tasks.todayFollowUps}</Cell>
        <Cell>{r.tasks.freshLeads}</Cell>
        <Cell color={r.tasks.missed ? 'var(--cr-warn)' : 'var(--cr-mut)'}>{r.tasks.missed}</Cell>
        <Cell bold br bg="var(--cr-accbg)">
          {r.tasks.total}
        </Cell>
        <Cell den={`/ ${r.tasks.total}`}>{r.tasks.achieved}</Cell>
        <Cell br pill={pill}>
          {achPct == null ? '-' : `${achPct}%`}
        </Cell>
        {/* Calling hours */}
        <Cell color="var(--cr-tx)">{H(r.callerdeskMinutes)}</Cell>
        <Cell color="var(--cr-wa)">{H(r.whatsappMinutes)}</Cell>
        <Cell bold br den="h">
          {H(r.callerdeskMinutes + r.whatsappMinutes)}
        </Cell>
        {/* First-call quality */}
        {dashCell()}
        {dashCell()}
        <Cell color={aPctColor}>{aPct == null ? '-' : `${aPct}%`}</Cell>
        <Cell color="var(--cr-mut)">{cPct == null ? '-' : `${cPct}%`}</Cell>
        <Cell bold br color={scoreColor(sc)}>
          {sc == null ? '-' : sc.toFixed(1)}
        </Cell>
        {/* Follow-up quality */}
        {dashCell()}
        {dashCell()}
        <Cell color={aPctColor}>{aPct == null ? '-' : `${aPct}%`}</Cell>
        <Cell color="var(--cr-mut)">{cPct == null ? '-' : `${cPct}%`}</Cell>
        <Cell bold br color={scoreColor(sc)}>
          {sc == null ? '-' : sc.toFixed(1)}
        </Cell>
        {/* Outbound */}
        <Cell bold>{r.outbound.total}</Cell>
        <Cell color="var(--cr-ok)">{r.outbound.accepted || ''}</Cell>
        <Cell color="var(--cr-warn)" bg={dashBg}>
          {r.outbound.missed || ''}
        </Cell>
        <Cell color="var(--cr-bad)" bg={dashBg} br>
          {r.outbound.declined || ''}
        </Cell>
        {/* Inbound */}
        <Cell bold>{r.inbound.total}</Cell>
        <Cell color="var(--cr-ok)">{r.inbound.accepted || ''}</Cell>
        <Cell color="var(--cr-warn)" bg={dashBg}>
          {r.inbound.missed || ''}
        </Cell>
        <Cell color="var(--cr-bad)" bg={dashBg}>
          {r.inbound.declined || ''}
        </Cell>
      </>
    );
  };

  const groupTh = (label: string, span: number, accent?: boolean): React.JSX.Element => (
    <th
      colSpan={span}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 3,
        background: accent ? 'var(--cr-accbg)' : 'var(--cr-sub)',
        color: accent ? 'var(--cr-acc)' : 'var(--cr-tx)',
        padding: '7px 10px',
        borderBottom: '1px solid var(--cr-bd)',
        borderRight: '1px solid var(--cr-bd)',
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </th>
  );

  const nameCell = (r: CallingReportRow, pad: number, kind: string): React.JSX.Element => (
    <td
      style={{
        position: 'sticky',
        left: 0,
        zIndex: 2,
        background: kind === 'total' ? 'var(--cr-sub)' : 'var(--cr-card)',
        padding: `0 10px 0 ${pad}px`,
        borderBottom: '1px solid var(--cr-bd2)',
        borderRight: '1px solid var(--cr-bd)',
        whiteSpace: 'nowrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {kind === 'tl' ? (
          expanded.has(r.id) ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )
        ) : (
          <span style={{ width: 14 }} />
        )}
        <span
          onClick={
            kind === 'agent' && onPickAgent
              ? (e) => {
                  e.stopPropagation();
                  onPickAgent(r.name);
                }
              : undefined
          }
          title={kind === 'agent' && onPickAgent ? 'Show this agent in the call log' : undefined}
          style={{
            fontWeight: kind === 'agent' ? 600 : 800,
            fontSize: kind === 'total' ? 13 : kind === 'tl' ? 12.5 : 12,
            color:
              activeAgent === r.name
                ? 'var(--cr-acc)'
                : kind === 'agent'
                  ? 'var(--cr-mut)'
                  : 'var(--cr-tx)',
            cursor: kind === 'agent' && onPickAgent ? 'pointer' : undefined,
            textDecoration: activeAgent === r.name ? 'underline' : undefined,
          }}
        >
          {r.name}
        </span>
        {r.agentCount != null && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--cr-mut)',
              background: 'var(--cr-sub2)',
              borderRadius: 999,
              padding: '1px 6px',
            }}
          >
            {kind === 'total' ? `${r.agentCount} agents` : r.agentCount}
          </span>
        )}
        {kind !== 'total' && (
          <CalendarRange
            className="ml-auto h-3.5 w-3.5 opacity-40"
            aria-label="Day-by-day breakdown (coming soon)"
          />
        )}
      </div>
    </td>
  );

  return (
    <div className="cr-scope border-border bg-card overflow-hidden rounded-2xl border shadow-sm">
      <style>{CR_TOKENS_CSS}</style>
      <div className="border-border/60 flex items-center justify-between gap-2 border-b px-4 py-2.5">
        <div className="text-muted-foreground text-xs">
          Click a team lead to expand agents.{' '}
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-[2px] border border-dashed"
              style={{ borderColor: '#d97706', background: 'var(--cr-warnbg)' }}
            />
            dashed = data coming online (per-call talk split, CallerDesk missed/declined)
          </span>
        </div>
        <button
          type="button"
          className="border-border hover:bg-muted rounded-md border px-2 py-1 text-xs font-medium"
          onClick={() => setExpanded(allOpen ? new Set() : new Set(groups.map((g) => g.tl.id)))}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div className="overflow-auto" style={{ maxHeight: 600 }}>
        <table style={{ minWidth: 1860, width: '100%', fontSize: 14 }}>
          <thead>
            <tr>
              <th
                rowSpan={2}
                style={{
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 4,
                  background: 'var(--cr-sub)',
                  textAlign: 'left',
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--cr-bd)',
                  borderRight: '1px solid var(--cr-bd)',
                  minWidth: 230,
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'var(--cr-mut)',
                }}
              >
                Team / Agent
              </th>
              {groupTh('Tasks', 6, true)}
              {groupTh('Calling hours', 3)}
              {groupTh('First-call quality', 5)}
              {groupTh('Follow-up quality', 5)}
              {groupTh('Outbound', 4)}
              {groupTh('Inbound', 4)}
            </tr>
            <tr>
              {SUBHEADS.map((h, i) => (
                <th
                  key={i}
                  title={h.dash ? 'Data coming online' : undefined}
                  style={{
                    position: 'sticky',
                    top: 31,
                    zIndex: 3,
                    background: 'var(--cr-sub)',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--cr-bd)',
                    borderRight: h.br ? '1px solid var(--cr-bd)' : undefined,
                    borderTop: h.dash ? DASH : undefined,
                    fontSize: 12.5,
                    fontWeight: h.bold ? 800 : 600,
                    color: h.bold ? 'var(--cr-tx)' : 'var(--cr-mut)',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={28} className="text-muted-foreground p-6 text-center">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && total && (
              <tr style={{ background: 'var(--cr-sub)', height: 40 }}>
                {nameCell(total, 12, 'total')}
                {cells(total)}
              </tr>
            )}
            {!loading &&
              groups.map((g) => (
                <React.Fragment key={g.tl.id}>
                  <tr
                    style={{ background: 'var(--cr-card)', height: 38, cursor: 'pointer' }}
                    onClick={() => toggle(g.tl.id)}
                  >
                    {nameCell(g.tl, 12, 'tl')}
                    {cells(g.tl)}
                  </tr>
                  {expanded.has(g.tl.id) &&
                    g.agents.map((a) => (
                      <tr key={a.id} style={{ background: 'var(--cr-card)', height: 36 }}>
                        {nameCell(a, 26, 'agent')}
                        {cells(a)}
                      </tr>
                    ))}
                </React.Fragment>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
